import { useEffect, useRef, useState } from "react";
import { listFiles, uploadFile, deleteFile } from "../lib/api";
import type { AppConfig, UploadedFile } from "../types";

interface Props {
  config: AppConfig;
  conversationId: string | null;
}

export function Attachments({ config, conversationId }: Props) {
  const [items, setItems] = useState<UploadedFile[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function carregar() {
    try {
      const r = await listFiles(config, conversationId);
      setItems(r.items);
    } catch (e: any) {
      setErr(e.message);
    }
  }

  useEffect(() => { carregar(); }, [conversationId, config]);

  async function enviarArquivos(files: FileList | File[]) {
    const arr = Array.from(files);
    if (!arr.length) return;
    setBusy(true);
    setErr(null);
    try {
      for (const f of arr) {
        await uploadFile(config, f, conversationId);
      }
      await carregar();
    } catch (e: any) {
      setErr(e.message || String(e));
    } finally {
      setBusy(false);
    }
  }

  async function apagar(id: string) {
    if (!confirm("Remover este arquivo?")) return;
    await deleteFile(config, id);
    setItems((prev) => prev.filter((x) => x.id !== id));
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) enviarArquivos(e.dataTransfer.files);
  }

  return (
    <div
      className={`attachments ${dragOver ? "drag-over" : ""}`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
    >
      <div className="att-head">
        <span className="muted">📎 Anexos {conversationId ? "desta conversa" : "(escolha uma conversa)"}</span>
        <button
          className="btn-secondary btn-sm"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          title="anexar arquivo (PDF, DOCX, XLSX, CSV, TXT...)"
        >
          {busy ? "enviando…" : "+ anexar"}
        </button>
        <input
          ref={inputRef}
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={(e) => e.target.files && enviarArquivos(e.target.files)}
        />
      </div>
      {err && <div className="error">⚠ {err}</div>}
      {items.length === 0 ? (
        <div className="muted att-empty">arraste arquivos aqui ou clique em "+ anexar"</div>
      ) : (
        <ul className="att-list">
          {items.map((f) => (
            <li key={f.id}>
              <span className="att-name">{f.original_name}</span>
              <span className="att-meta">
                {(f.size_bytes / 1024).toFixed(0)} KB
                {f.extracted_chars ? ` · ${f.extracted_chars} chars extraídos` : ""}
              </span>
              <button onClick={() => apagar(f.id)} className="btn-tiny" title="remover">✕</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
